// src/pages/ArticlesList.jsx

import React, { useState, useMemo, useEffect } from "react";
import { useArticlesContext } from "../context/ArticlesContext";
import HomeArticlesGrid from "../components/HomeArticlesGrid";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSearchParams } from "react-router-dom";

/**
 * Helper function to shuffle an array in place using the Fisher-Yates algorithm.
 */
function shuffleArray(array) {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
}

/**
 * Helper to filter articles by single category (optional) + multiple labels (AND logic).
 */
function filterArticles(articles, labels, category, selectedLabels) {
  let filtered = articles;

  // 1) If category is chosen, keep only articles that have at least
  //    one label in that category
  if (category) {
    filtered = filtered.filter((article) => {
      if (!article.labels) return false;
      return article.labels.some((labelId) => {
        const lblObj = labels.find((l) => l.id === labelId);
        return lblObj && lblObj.category === category;
      });
    });
  }

  // 2) If selectedLabels is not empty, all those labels must be included (AND logic).
  if (selectedLabels.length > 0) {
    filtered = filtered.filter((article) => {
      if (!article.labels) return false;
      return selectedLabels.every((lblId) => article.labels.includes(lblId));
    });
  }

  return filtered;
}

export default function ArticlesList() {
  const { articles, labels } = useArticlesContext();

  // Single category selection
  const [selectedCategory, setSelectedCategory] = useState("");
  // Multiple label selection (checkboxes)
  const [selectedLabels, setSelectedLabels] = useState([]);
  // Toggle to show/hide the filter panel
  const [showFilters, setShowFilters] = useState(true);

  // NEW: Read query params to set initial filters
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // If ?category=xxx, set the category state
    const cat = searchParams.get("category") || "";
    setSelectedCategory(cat);

    // If ?labels=xxx, can be comma-separated for multiple
    const labsStr = searchParams.get("labels") || "";
    const labsArr = labsStr ? labsStr.split(",") : [];
    setSelectedLabels(labsArr);
  }, [searchParams]);

  // 1) Collect unique categories from labels
  const allCategories = useMemo(() => {
    const cats = labels.map((lbl) => lbl.category).filter(Boolean);
    return [...new Set(cats)].sort();
  }, [labels]);

  // 2) We'll next figure out how many articles result if we remove/add each label,
  //    but first we need a "base" set of articles to check.

  // 3) Compute how many articles for each category, given current 'selectedLabels'
  const categoryArticleCountMap = useMemo(() => {
    const result = {};
    allCategories.forEach((cat) => {
      const hypothetical = filterArticles(articles, labels, cat, selectedLabels);
      result[cat] = hypothetical.length;
    });
    return result;
  }, [allCategories, articles, labels, selectedLabels]);

  // 4) For each label in *all labels*, see how many articles if we toggle it on/off
  const labelArticleCountMap = useMemo(() => {
    const result = {};
    labels.forEach((lbl) => {
      let hypotheticalLabels = [];
      if (selectedLabels.includes(lbl.id)) {
        // If it's currently selected, removing it
        hypotheticalLabels = selectedLabels.filter((x) => x !== lbl.id);
      } else {
        // Not selected, adding it
        hypotheticalLabels = [...selectedLabels, lbl.id];
      }
      const hypothetical = filterArticles(articles, labels, selectedCategory, hypotheticalLabels);
      result[lbl.id] = hypothetical.length;
    });
    return result;
  }, [articles, labels, selectedCategory, selectedLabels]);

  // 5) Decide which labels to display in checkboxes
  //    (only those with a count > 0, so we don't show labels that yield 0 results)
  const displayLabels = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }
    return labels.filter((lbl) => {
      if (lbl.category !== selectedCategory) return false;
      // Only keep labels that yield at least one article result
      const count = labelArticleCountMap[lbl.id] || 0;
      return count > 0;
    });
  }, [labels, selectedCategory, labelArticleCountMap]);

  // 6) Build the final list of articles based on the actual filters,
  //    then shuffle them. Also store original full-size .image if needed.
  const finalArticles = useMemo(() => {
    const filtered = filterArticles(articles, labels, selectedCategory, selectedLabels);

    const processed = filtered.map((art) => {
      const copy = { ...art };
      // Keep a copy of the original image
      copy.imageOriginal = copy.image;
      // If it ends with .jpg, produce the -low version
      if (copy.image && copy.image.endsWith(".jpg")) {
        copy.image = copy.image.replace(".jpg", "-low.jpg");
      }
      return copy;
    });

    return shuffleArray(processed);
  }, [articles, labels, selectedCategory, selectedLabels]);

  // Clear all filters
  const resetFilters = () => {
    setSelectedCategory("");
    setSelectedLabels([]);
  };

  // Toggle a label in the selectedLabels array
  const handleToggleLabel = (labelId) => {
    setSelectedLabels((prev) =>
      prev.includes(labelId) ? prev.filter((x) => x !== labelId) : [...prev, labelId]
    );
  };

  return (
    <>
      <Navbar />

      {/* Hero banner */}
      <section
        className="page-banner"
        style={{ backgroundImage: 'url("/assets/images/hero.jpg")' }}
      >
        <div className="banner-content container">
          <h1>All Articles</h1>
          <p>Explore our detailed guides, tips, and insights.</p>
        </div>
      </section>

      <main>
        <div className="container">
          {/* Toggle button to show/hide filters */}
          <div className="filter-toggle-wrapper">
            <button
              className="filter-toggle-btn"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* Collapsible Filter Section */}
          <section className={`filter-section ${showFilters ? "open" : "closed"}`}>
            <h2>Filter Articles</h2>

            {/* Category + Clear Filters */}
            <form
              className="filter-form"
              onSubmit={(e) => e.preventDefault()}
              style={{ marginBottom: "1.5rem" }}
            >
              {/* Category Dropdown */}
              <div className="filter-group">
                <label htmlFor="categorySelect">Category</label>
                <select
                  id="categorySelect"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">
                    -- All Categories ({articles.length}) --
                  </option>
                  {allCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)} (
                      {categoryArticleCountMap[cat] || 0})
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters Button */}
              <div className="filter-actions">
                <button
                  type="button"
                  className="reset-button"
                  onClick={resetFilters}
                >
                  Clear Filters
                </button>
              </div>
            </form>

            {/* Multiple label checkboxes (only show if a category is selected) */}
            {selectedCategory && (
              <div className="labels-checkboxes">
                {displayLabels.length === 0 && (
                  <p className="no-labels-hint">
                    No labels available for this category.
                  </p>
                )}
                {displayLabels.map((lbl) => {
                  const isChecked = selectedLabels.includes(lbl.id);
                  const count = labelArticleCountMap[lbl.id] || 0;
                  return (
                    <label className="label-checkbox" key={lbl.id}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleLabel(lbl.id)}
                      />
                      {lbl.name} ({count})
                    </label>
                  );
                })}
              </div>
            )}
          </section>

          {/* Final Articles Grid (randomly ordered now), 
              with .image set to -low.jpg but fallback info in .imageOriginal */}
          <section className="articles">
            <HomeArticlesGrid showAll articlesOverride={finalArticles} />
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}